import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  getContainerHierarchy, 
  getContainerChildren, 
  getContainerItems,
  createContainer,
  createItem,
  updateContainer,
  deleteContainer
} from '../services/api';
import { ArrowLeft, Plus, Package, Box, QrCode as QrCodeIcon, Trash2, Edit2 } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

const ContainerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [container, setContainer] = useState(null);
  const [children, setChildren] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showContainerForm, setShowContainerForm] = useState(false);
  const [showItemForm, setShowItemForm] = useState(false);
  const [showQR, setShowQR] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [containerFormData, setContainerFormData] = useState({ name: '', description: '' });
  const [itemFormData, setItemFormData] = useState({ name: '', description: '', barcode: '', quantity: 1 });
  const [editFormData, setEditFormData] = useState({ name: '', description: '' });

  const fetchData = async () => {
    try {
      const [containerRes, childrenRes, itemsRes] = await Promise.all([
        getContainerHierarchy(id),
        getContainerChildren(id),
        getContainerItems(id)
      ]);
      setContainer(containerRes.data);
      setChildren(childrenRes.data);
      setItems(itemsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (container) {
      setEditFormData({
        name: container.name,
        description: container.description || ''
      });
    }
  }, [container]);

  const handleContainerSubmit = async (e) => {
    e.preventDefault();
    try {
      await createContainer({
        ...containerFormData,
        location_id: container.location_id,
        parent_container_id: id
      });
      setContainerFormData({ name: '', description: '' });
      setShowContainerForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating container:', error);
    }
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    try {
      await createItem({
        ...itemFormData,
        container_id: id
      });
      setItemFormData({ name: '', description: '', barcode: '', quantity: 1 });
      setShowItemForm(false);
      fetchData();
    } catch (error) {
      console.error('Error creating item:', error);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await updateContainer(id, editFormData);
      setShowEditForm(false);
      fetchData();
    } catch (error) {
      console.error('Error updating container:', error);
      alert('Failed to update container. Please try again.');
    }
  };

  const handleDelete = async () => {
    const hasChildren = children.length > 0;
    const hasItems = items.length > 0;
    
    let confirmMessage = `Are you sure you want to delete "${container.name}"?`;
    
    if (hasChildren || hasItems) {
      confirmMessage += '\n\nThis container contains:';
      if (hasChildren) confirmMessage += `\n- ${children.length} child container(s)`;
      if (hasItems) confirmMessage += `\n- ${items.length} item(s)`;
      confirmMessage += '\n\nAll contents will be deleted permanently. This action cannot be undone.';
    } else {
      confirmMessage += ' This action cannot be undone.';
    }
    
    if (window.confirm(confirmMessage)) {
      try {
        await deleteContainer(id);
        // Navigate to parent container or location
        if (container.parent_container_id) {
          navigate(`/containers/${container.parent_container_id}`);
        } else if (container.location_id) {
          navigate(`/locations/${container.location_id}`);
        } else {
          navigate('/');
        }
      } catch (error) {
        console.error('Error deleting container:', error);
        alert('Failed to delete container. Please try again.');
      }
    }
  };

  if (loading) return <div className="container">Loading...</div>;
  if (!container) return <div className="container">Container not found</div>;

  return (
    <div className="container">
      <button onClick={() => navigate(-1)} className="button button-secondary" style={{ marginBottom: '20px' }}>
        <ArrowLeft size={20} style={{ display: 'inline', marginRight: '5px' }} />
        Back
      </button>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <h1>
              <Package size={28} style={{ display: 'inline', marginRight: '10px' }} />
              {container.name}
            </h1>
            {container.description && <p style={{ color: '#666', marginTop: '10px' }}>{container.description}</p>}
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={() => setShowEditForm(!showEditForm)} className="button button-secondary">
              <Edit2 size={20} />
            </button>
            <button onClick={() => setShowQR(!showQR)} className="button button-secondary">
              <QrCodeIcon size={20} />
            </button>
            <button onClick={handleDelete} className="button button-danger">
              <Trash2 size={20} />
            </button>
          </div>
        </div>

        {showQR && (
          <div style={{ marginTop: '20px', textAlign: 'center', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <QRCodeSVG value={container.id} size={200} />
            <p style={{ marginTop: '10px', fontSize: '12px', color: '#666', wordBreak: 'break-all' }}>
              {container.id}
            </p>
          </div>
        )}

        {showEditForm && (
          <div style={{ marginTop: '20px', padding: '20px', backgroundColor: '#f9f9f9', borderRadius: '8px' }}>
            <h3>Edit Container</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="form-group">
                <label className="label">Name</label>
                <input
                  type="text"
                  className="input"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label className="label">Description</label>
                <textarea
                  className="input"
                  rows="3"
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button type="submit" className="button button-success">
                  Save Changes
                </button>
                <button 
                  type="button" 
                  onClick={() => {
                    setShowEditForm(false);
                    setEditFormData({
                      name: container.name,
                      description: container.description || ''
                    });
                  }} 
                  className="button button-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {/* Child Containers */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '20px', marginBottom: '20px' }}>
        <h2>Sub-Containers</h2>
        <button onClick={() => setShowContainerForm(!showContainerForm)} className="button">
          <Plus size={20} style={{ display: 'inline', marginRight: '5px' }} />
          Add Container
        </button>
      </div>

      {showContainerForm && (
        <div className="card">
          <h3>New Container</h3>
          <form onSubmit={handleContainerSubmit}>
            <div className="form-group">
              <label className="label">Name</label>
              <input
                type="text"
                className="input"
                value={containerFormData.name}
                onChange={(e) => setContainerFormData({ ...containerFormData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="label">Description</label>
              <textarea
                className="input"
                rows="3"
                value={containerFormData.description}
                onChange={(e) => setContainerFormData({ ...containerFormData, description: e.target.value })}
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="button">Create</button>
              <button type="button" onClick={() => setShowContainerForm(false)} className="button button-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid">
        {children.map((child) => (
          <div
            key={child.id}
            className="list-item"
            onClick={() => navigate(`/containers/${child.id}`)}
          >
            <h3>
              <Package size={20} style={{ display: 'inline', marginRight: '8px' }} />
              {child.name}
            </h3>
            {child.description && (
              <p style={{ color: '#666', marginTop: '8px' }}>{child.description}</p>
            )}
          </div>
        ))}
      </div>

      {/* Items */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '30px', marginBottom: '20px' }}>
        <h2>Items</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button 
            onClick={() => navigate('/scan', { 
              state: { 
                containerId: id, 
                containerName: container.name 
              } 
            })} 
            className="button button-success"
          >
            <QrCodeIcon size={20} style={{ display: 'inline', marginRight: '5px' }} />
            Scan Item
          </button>
          <button onClick={() => setShowItemForm(!showItemForm)} className="button button-success">
            <Plus size={20} style={{ display: 'inline', marginRight: '5px' }} />
            Add Item
          </button>
        </div>
      </div>

      {showItemForm && (
        <div className="card">
          <h3>New Item</h3>
          <form onSubmit={handleItemSubmit}>
            <div className="form-group">
              <label className="label">Name</label>
              <input
                type="text"
                className="input"
                value={itemFormData.name}
                onChange={(e) => setItemFormData({ ...itemFormData, name: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label className="label">Description</label>
              <textarea
                className="input"
                rows="3"
                value={itemFormData.description}
                onChange={(e) => setItemFormData({ ...itemFormData, description: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Barcode (optional)</label>
              <input
                type="text"
                className="input"
                value={itemFormData.barcode}
                onChange={(e) => setItemFormData({ ...itemFormData, barcode: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label className="label">Quantity</label>
              <input
                type="number"
                className="input"
                min="1"
                value={itemFormData.quantity}
                onChange={(e) => setItemFormData({ ...itemFormData, quantity: parseInt(e.target.value) })}
                required
              />
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="submit" className="button">Create</button>
              <button type="button" onClick={() => setShowItemForm(false)} className="button button-secondary">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid">
        {items.map((item) => (
          <div
            key={item.id}
            className="list-item"
            onClick={() => navigate(`/items/${item.id}`)}
          >
            <h3>
              <Box size={20} style={{ display: 'inline', marginRight: '8px' }} />
              {item.name}
            </h3>
            {item.barcode && (
              <p style={{ fontSize: '12px', color: '#007bff', marginTop: '5px' }}>
                Barcode: {item.barcode}
              </p>
            )}
            <p style={{ fontSize: '14px', marginTop: '5px' }}>
              Quantity: {item.quantity}
            </p>
          </div>
        ))}
      </div>

      {children.length === 0 && items.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: '#666' }}>
          <p>This container is empty. Add containers or items to get started!</p>
        </div>
      )}
    </div>
  );
};

export default ContainerDetail;
